// React Query hooks over ContractModelService (contract model v2). Every read
// is keyed under ['cm', programId, ...]. A write refreshes the reads it can
// change (SAD-993, `cmWriteKeys`): a date-level write (a submission, a review,
// a proposal) its dates and decisions; a deliverable-level write that
// deliverable, every date, and the team reads its routing and SOW gaps feed;
// a settings change still the whole program. Before, every write refreshed
// the whole program, so one save on a deliverable refetched every team's and
// every deliverable's detail.
//
// Each request wraps pangaea's own message as `body`; these hooks take and
// return those inner messages so callers never see the wrapper.
import { Timestamp } from '@bufbuild/protobuf';
import { ContractModelTypes as CM } from '@mono/proto-clients';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useUploadArtifacts } from '../../../hooks/useDeliveryDocuments';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { contractModelClient, deliveryClient } from '../../../lib/connect/clients';
import { DOC_KEY } from '../Docs/hooks';
export const OccurrenceWindow = CM.OccurrenceWindow;
export const SowGapChoice = CM.SowGapChoice;
export const CM_KEY = 'cm';
const STALE = 15000;
export const cmKeys = {
    all: (programId)=>[
            CM_KEY,
            programId
        ],
    duties: (programId, includeRules = false)=>[
            CM_KEY,
            programId,
            'duties',
            includeRules
        ],
    /**
   * SAD-994: every listed duty with its GetDuty answer, one read; SAD-997:
   * with a team, only its own SOW duties.
   */ dutiesDetail: (programId, workstreamId = '')=>[
            CM_KEY,
            programId,
            'duties',
            'detail',
            workstreamId
        ],
    duty: (programId, dutyId)=>[
            CM_KEY,
            programId,
            'duty',
            dutyId
        ],
    occurrences: (programId, req)=>[
            CM_KEY,
            programId,
            'occurrences',
            JSON.stringify(req)
        ],
    /**
   * SAD-997: every team's counts in its own current window, one read. Under
   * the date lists' prefix, so every write that refreshes the dates
   * refreshes these.
   */ workstreamWindowCounts: (programId)=>[
            CM_KEY,
            programId,
            'occurrences',
            'workstream-window-counts'
        ],
    occurrence: (programId, occId)=>[
            CM_KEY,
            programId,
            'occurrence',
            occId
        ],
    decisions: (programId, req)=>[
            CM_KEY,
            programId,
            'decisions',
            JSON.stringify(req)
        ],
    workstreams: (programId)=>[
            CM_KEY,
            programId,
            'workstreams'
        ],
    workstream: (programId, wsId)=>[
            CM_KEY,
            programId,
            'workstream',
            wsId
        ],
    /** SAD-994: every team with its GetWorkstream answer, one read. */ workstreamsDetail: (programId)=>[
            CM_KEY,
            programId,
            'workstream-details'
        ],
    settings: (programId)=>[
            CM_KEY,
            programId,
            'settings'
        ],
    workProducts: (programId, req)=>[
            CM_KEY,
            programId,
            'work-products',
            JSON.stringify(req)
        ],
    members: (programId, wsId)=>[
            CM_KEY,
            programId,
            'members',
            wsId
        ],
    duplicates: (programId, wsId, title)=>[
            CM_KEY,
            programId,
            'wp-duplicates',
            wsId,
            title
        ],
    comments: (programId, t)=>[
            CM_KEY,
            programId,
            'comments',
            t.type,
            t.id
        ]
};
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
export function useDuties(programId, { includeRules = false } = {}) {
    return useQuery({
        queryKey: cmKeys.duties(programId, includeRules),
        enabled: !!programId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listDuties({
                body: {
                    programId,
                    includeRules
                }
            });
            return resp.body ?? new CM.ListDutiesResponse();
        }
    });
}
export function useDuty(programId, dutyId) {
    return useQuery({
        queryKey: cmKeys.duty(programId, dutyId ?? ''),
        enabled: !!programId && !!dutyId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.getDuty({
                body: {
                    programId,
                    dutyId
                }
            });
            return resp.body ?? new CM.GetDutyResponse();
        }
    });
}
/**
 * Every deliverable's GetDuty answer (its SOW assignments and checks), by
 * duty id, in one ListDuties call (SAD-994), never a GetDuty per deliverable.
 * With a team (SAD-997), only the deliverables one of its SOWs assigns:
 * pangaea plans and sends no other. `listed` is every deliverable the call
 * listed, so a listed one without its detail is still named.
 */ export function useDutiesDetail(programId, { enabled = true, workstreamId = '' } = {}) {
    return useQuery({
        queryKey: cmKeys.dutiesDetail(programId, workstreamId),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listDuties({
                body: workstreamId ? {
                    programId,
                    includeDetail: true,
                    workstreamId
                } : {
                    programId,
                    includeDetail: true
                }
            });
            const byId = new Map();
            for (const d of resp.body?.details ?? [])if (d.duty?.id) byId.set(d.duty.id, d);
            return {
                byId,
                listed: resp.body?.duties ?? []
            };
        }
    });
}
export async function listOccurrences(programId, req) {
    const resp = await contractModelClient.listOccurrences({
        body: {
            programId,
            ...req
        }
    });
    return resp.body ?? new CM.ListOccurrencesResponse();
}
export function useOccurrences(programId, req, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.occurrences(programId, req),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: ()=>listOccurrences(programId, req)
    });
}
/** Several ListOccurrences on one program at once (e.g. each team's window). */ export function useOccurrencesMany(programId, reqs) {
    return useQueries({
        queries: reqs.map((req)=>({
                queryKey: cmKeys.occurrences(programId, req),
                enabled: !!programId,
                staleTime: STALE,
                queryFn: ()=>listOccurrences(programId, req)
            }))
    });
}
/**
 * Every team's counts in its own current window (state counts, as each
 * team's ListOccurrences with WORKSTREAM_CURRENT_WINDOW counts them), by team
 * id, in one ListWorkstreamWindowCounts call (SAD-997), never a
 * ListOccurrences per team.
 */ export function useWorkstreamWindowCounts(programId) {
    return useQuery({
        queryKey: cmKeys.workstreamWindowCounts(programId),
        enabled: !!programId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listWorkstreamWindowCounts({
                body: {
                    programId
                }
            });
            return new Map((resp.body?.workstreams ?? []).map((c)=>[
                    c.workstreamId,
                    c
                ]));
        }
    });
}
export function useOccurrence(programId, occId, asOf = '') {
    return useQuery({
        queryKey: cmKeys.occurrence(programId, occId ?? ''),
        enabled: !!programId && !!occId,
        staleTime: STALE,
        retry: false,
        queryFn: async ()=>{
            const resp = await contractModelClient.getOccurrence({
                body: {
                    programId,
                    occurrenceId: occId,
                    asOf
                }
            });
            return resp.body ?? new CM.GetOccurrenceResponse();
        }
    });
}
export function useOccurrenceDecisions(programId, req, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.decisions(programId, req),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listOccurrenceDecisions({
                body: {
                    programId,
                    ...req
                }
            });
            return resp.body ?? new CM.ListOccurrenceDecisionsResponse();
        }
    });
}
/** The program's open proposals, by the date they are on. */ export function useAwaitingApproval(programId) {
    const query = useOccurrenceDecisions(programId, {
        openOnly: true
    });
    const { data } = query;
    const maps = useMemo(()=>{
        const occById = new Map((data?.occurrences ?? []).map((o)=>[
                o.id,
                o
            ]));
        const byOccurrence = new Map();
        for (const d of data?.decisions ?? [])byOccurrence.set(d.occurrenceId, d);
        return {
            occById,
            byOccurrence
        };
    }, [
        data
    ]);
    return {
        query,
        ...maps
    };
}
export function useWorkstreams(programId, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.workstreams(programId),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listWorkstreams({
                body: {
                    programId
                }
            });
            return resp.body?.workstreams ?? [];
        }
    });
}
export function useWorkstream(programId, wsId) {
    return useQuery({
        queryKey: cmKeys.workstream(programId, wsId ?? ''),
        enabled: !!programId && !!wsId,
        staleTime: STALE,
        retry: false,
        queryFn: async ()=>{
            const resp = await contractModelClient.getWorkstream({
                body: {
                    programId,
                    workstreamId: wsId
                }
            });
            return resp.body ?? new CM.GetWorkstreamResponse();
        }
    });
}
/**
 * Every team's GetWorkstream answer (SOWs, lineage, lines), in one
 * ListWorkstreams call (SAD-994), never a GetWorkstream per team; for
 * pickers and grouping.
 */ export function useWorkstreamDetails(programId, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.workstreamsDetail(programId),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listWorkstreams({
                body: {
                    programId,
                    includeDetail: true
                }
            });
            return resp.body?.details ?? [];
        }
    });
}
export function useDeliverableSettings(programId) {
    return useQuery({
        queryKey: cmKeys.settings(programId),
        enabled: !!programId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.getDeliverableSettings({
                body: {
                    programId
                }
            });
            return resp.body?.settings ?? new CM.DeliverableSettings();
        }
    });
}
const listsOf = (p, read)=>[
        CM_KEY,
        p,
        read
    ];
/** One deliverable's detail, or every mounted one when the write cannot name it. */ const dutyKey = (p, id)=>id ? cmKeys.duty(p, id) : listsOf(p, 'duty');
// Pangaea reads a date's status from its submissions and decisions; GetDuty,
// GetWorkstream and ListDuties read none of those (rules, assignments, SOWs,
// settings, events and periods only). So a date-level write changes the date
// lists, the open date and the decisions, and nothing else.
const DATES = (p)=>[
        listsOf(p, 'occurrences'),
        listsOf(p, 'occurrence'),
        listsOf(p, 'decisions')
    ];
// An approval applies its change set, which may log an event that dates other
// deliverables: their dates, and the list's checks and undated reasons. Its
// own deliverable's detail is the one named in the answer.
const APPLIED = (p, ids)=>[
        ...DATES(p),
        listsOf(p, 'duties'),
        dutyKey(p, ids.dutyId)
    ];
// A rule, a deliverable, an assignment or a SOW gap ruling re-plans the
// deliverable: its detail, the list's checks, routings and watches, every
// date, each team's open SOW gaps (GetWorkstream plans every deliverable),
// and the team's SOW-deliverable matches a work product is checked against.
const PLANNED = (p, ids)=>[
        ...APPLIED(p, ids),
        listsOf(p, 'workstream'),
        listsOf(p, 'workstream-details'),
        listsOf(p, 'wp-duplicates')
    ];
// A team, its lines, a SOW, an exercise link or a logged event changes what
// is in force for every deliverable: every deliverable's detail and the teams.
const REPLANNED = (p)=>[
        ...PLANNED(p, {}),
        cmKeys.workstreams(p),
        listsOf(p, 'work-products')
    ];
/** Every contract-model write, by its ContractModelService method, and what it refreshes. */ const CM_WRITES = {
    createSubmission: DATES,
    withdrawSubmission: DATES,
    recordReview: DATES,
    proposeOccurrenceWaiver: DATES,
    proposeOccurrenceExtension: DATES,
    proposeOccurrenceNotOwed: DATES,
    proposeSubmissionEdit: DATES,
    proposeSubmissionEvidence: DATES,
    proposeWaiverEvidence: DATES,
    proposeWaiverEdit: DATES,
    recordOccurrenceDecisionCorAnswer: DATES,
    rejectOccurrenceDecision: DATES,
    moveOccurrence: DATES,
    removeOccurrence: DATES,
    addOccurrence: APPLIED,
    approveOccurrenceDecision: APPLIED,
    createDuty: (p, ids)=>[
            ...PLANNED(p, ids),
            listsOf(p, 'work-products')
        ],
    updateDuty: PLANNED,
    deleteDuty: (p, ids)=>[
            ...PLANNED(p, ids),
            listsOf(p, 'work-products')
        ],
    createScheduleRule: PLANNED,
    updateScheduleRule: PLANNED,
    deleteScheduleRule: PLANNED,
    setDutyAssignment: PLANNED,
    ruleSowGap: (p)=>PLANNED(p, {}),
    promoteWorkProduct: (p, ids)=>[
            ...PLANNED(p, ids),
            listsOf(p, 'work-products')
        ],
    logContractEvent: REPLANNED,
    createWorkstream: REPLANNED,
    updateWorkstream: REPLANNED,
    createSow: REPLANNED,
    setExerciseWorkstream: REPLANNED,
    updateDeliverableSettings: (p)=>[
            cmKeys.all(p)
        ],
    createWorkProduct: (p)=>[
            listsOf(p, 'work-products')
        ],
    updateWorkProduct: (p)=>[
            listsOf(p, 'work-products')
        ],
    assessWorkProducts: (p)=>[
            listsOf(p, 'work-products')
        ],
    addWorkstreamMember: (p)=>[
            listsOf(p, 'members')
        ],
    updateWorkstreamMember: (p)=>[
            listsOf(p, 'members')
        ],
    removeWorkstreamMember: (p)=>[
            listsOf(p, 'members')
        ]
};
/** The query keys (prefixes) a write can change. */ export function cmWriteKeys(write, programId, ids) {
    return CM_WRITES[write](programId, ids);
}
const idOf = (v)=>typeof v === 'string' && v ? v : undefined;
/** The deliverable a write names: in its request, or in what it answers. */ function writeIds(vars, result) {
    const v = vars ?? {};
    const r = result ?? {};
    return {
        dutyId: idOf(v.dutyId) ?? idOf(r.duty?.id) ?? idOf(r.rule?.dutyId) ?? idOf(r.assignment?.dutyId) ?? idOf(r.occurrence?.dutyId)
    };
}
/** Refresh what a write changed; only the reads on screen refetch now. */ export function useCmInvalidate(programId) {
    const qc = useQueryClient();
    return useCallback((write, ids = {})=>Promise.all(cmWriteKeys(write, programId, ids).map((queryKey)=>qc.invalidateQueries({
                queryKey,
                refetchType: 'active'
            }))), [
        qc,
        programId
    ]);
}
function useCmMutation(programId, write, fn, { success, errors = 'toast', failure = 'Could not save', deferRefresh = false } = {}) {
    const refresh = useCmInvalidate(programId);
    return useMutation({
        mutationFn: fn,
        onSuccess: (r, vars)=>{
            if (!deferRefresh) void refresh(write, writeIds(vars, r));
            if (success) toast.success(typeof success === 'function' ? success(r) : success);
        },
        onError: (err)=>{
            if (errors === 'toast') toast.error(apiErrorMessage(err, failure));
        }
    });
}
export function useCreateDuty(programId, opts) {
    return useCmMutation(programId, 'createDuty', async (b)=>(await contractModelClient.createDuty({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateDutyResponse(), opts);
}
export function useUpdateDuty(programId, opts) {
    return useCmMutation(programId, 'updateDuty', async (b)=>(await contractModelClient.updateDuty({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateDutyResponse(), opts);
}
export function useDeleteDuty(programId, opts) {
    return useCmMutation(programId, 'deleteDuty', async (b)=>(await contractModelClient.deleteDuty({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.DeleteDutyResponse(), opts);
}
export function useCreateRule(programId, opts) {
    return useCmMutation(programId, 'createScheduleRule', async (b)=>(await contractModelClient.createScheduleRule({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateScheduleRuleResponse(), opts);
}
export function useUpdateRule(programId, opts) {
    return useCmMutation(programId, 'updateScheduleRule', async (b)=>(await contractModelClient.updateScheduleRule({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateScheduleRuleResponse(), opts);
}
export function useDeleteRule(programId, opts) {
    return useCmMutation(programId, 'deleteScheduleRule', async (b)=>(await contractModelClient.deleteScheduleRule({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.DeleteScheduleRuleResponse(), opts);
}
export function useAddOccurrence(programId, opts) {
    return useCmMutation(programId, 'addOccurrence', async (b)=>(await contractModelClient.addOccurrence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.AddOccurrenceResponse(), opts);
}
export function useRemoveOccurrence(programId, opts) {
    return useCmMutation(programId, 'removeOccurrence', async (b)=>(await contractModelClient.removeOccurrence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RemoveOccurrenceResponse(), opts);
}
export function useMoveOccurrence(programId, opts) {
    return useCmMutation(programId, 'moveOccurrence', async (b)=>(await contractModelClient.moveOccurrence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.MoveOccurrenceResponse(), opts);
}
export function useProposeWaiver(programId, opts) {
    return useCmMutation(programId, 'proposeOccurrenceWaiver', async (b)=>(await contractModelClient.proposeOccurrenceWaiver({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeOccurrenceWaiverResponse(), opts);
}
/** SAD-834: an extension (a new due date) a second person approves. */ export function useProposeExtension(programId, opts) {
    return useCmMutation(programId, 'proposeOccurrenceExtension', async (b)=>(await contractModelClient.proposeOccurrenceExtension({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeOccurrenceExtensionResponse(), opts);
}
/** SAD-902: correct a recorded submission; a second person approves. */ export function useProposeSubmissionEdit(programId, opts) {
    return useCmMutation(programId, 'proposeSubmissionEdit', async (b)=>(await contractModelClient.proposeSubmissionEdit({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeSubmissionEditResponse(), opts);
}
/** SAD-902: add files or links to a recorded submission; a second person approves. */ export function useProposeSubmissionEvidence(programId, opts) {
    return useCmMutation(programId, 'proposeSubmissionEvidence', async (b)=>(await contractModelClient.proposeSubmissionEvidence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeSubmissionEvidenceResponse(), opts);
}
/** SAD-1010: add files or links to an approved waiver; a second person approves. */ export function useProposeWaiverEvidence(programId, opts) {
    return useCmMutation(programId, 'proposeWaiverEvidence', async (b)=>(await contractModelClient.proposeWaiverEvidence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeWaiverEvidenceResponse(), opts);
}
/** Change an approved waiver's COR date, reason or document; a second person approves. */ export function useProposeWaiverEdit(programId, opts) {
    return useCmMutation(programId, 'proposeWaiverEdit', async (b)=>(await contractModelClient.proposeWaiverEdit({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeWaiverEditResponse(), opts);
}
/**
 * Uploads one evidence file (an .eml from the CO, a signed letter) and records
 * it as a program document, the record a waiver's or an extension's evidence
 * names. Resolves to the document id; it also lists on the program's Docs.
 */ export function useUploadEvidence(programId) {
    const upload = useUploadArtifacts();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (file)=>{
            const up = await upload.mutateAsync({
                files: [
                    file
                ]
            });
            const artifact = up.files[0];
            if (!artifact) throw new Error(`Could not upload ${file.name}`);
            const today = new Date();
            const resp = await deliveryClient.createProgramDocument({
                programId,
                title: file.name,
                description: 'Evidence attached to a deliverable date decision',
                // doc_date is a calendar day anchored at UTC midnight (Docs tab).
                docDate: Timestamp.fromDate(new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))),
                fileId: artifact.artifactId,
                // The server resolves the filename and the uploader itself.
                fileName: '',
                category: 'Other',
                uploadedBy: '',
                companyAssignmentId: '',
                contactId: '',
                issueId: ''
            });
            const id = resp.document?.id;
            if (!id) throw new Error(`Could not record ${file.name}`);
            return id;
        },
        onSuccess: ()=>void qc.invalidateQueries({
                queryKey: [
                    DOC_KEY
                ]
            })
    });
}
export function useProposeNotOwed(programId, opts) {
    return useCmMutation(programId, 'proposeOccurrenceNotOwed', async (b)=>(await contractModelClient.proposeOccurrenceNotOwed({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeOccurrenceNotOwedResponse(), opts);
}
/** SAD-866: the COR's answer to a proposed waiver or extension. */ export function useRecordCorAnswer(programId, opts) {
    return useCmMutation(programId, 'recordOccurrenceDecisionCorAnswer', async (b)=>(await contractModelClient.recordOccurrenceDecisionCorAnswer({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RecordOccurrenceDecisionCorAnswerResponse(), opts);
}
export function useApproveDecision(programId, opts) {
    return useCmMutation(programId, 'approveOccurrenceDecision', async (b)=>(await contractModelClient.approveOccurrenceDecision({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ApproveOccurrenceDecisionResponse(), opts);
}
export function useRejectDecision(programId, opts) {
    return useCmMutation(programId, 'rejectOccurrenceDecision', async (b)=>(await contractModelClient.rejectOccurrenceDecision({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RejectOccurrenceDecisionResponse(), opts);
}
export function useLogContractEvent(programId, opts) {
    return useCmMutation(programId, 'logContractEvent', async (b)=>(await contractModelClient.logContractEvent({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.LogContractEventResponse(), opts);
}
export function useCreateWorkstream(programId, opts) {
    return useCmMutation(programId, 'createWorkstream', async (b)=>(await contractModelClient.createWorkstream({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateWorkstreamResponse(), opts);
}
/** SAD-861: rename a team, or replace the base and option-period lines it owns. */ export function useUpdateWorkstream(programId, opts) {
    return useCmMutation(programId, 'updateWorkstream', async (b)=>(await contractModelClient.updateWorkstream({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateWorkstreamResponse(), opts);
}
export function useCreateSow(programId, opts) {
    return useCmMutation(programId, 'createSow', async (b)=>(await contractModelClient.createSow({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateSowResponse(), opts);
}
export function useSetExerciseWorkstream(programId, opts) {
    return useCmMutation(programId, 'setExerciseWorkstream', async (b)=>(await contractModelClient.setExerciseWorkstream({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.SetExerciseWorkstreamResponse(), opts);
}
export function useSetDutyAssignment(programId, opts) {
    return useCmMutation(programId, 'setDutyAssignment', async (b)=>(await contractModelClient.setDutyAssignment({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.SetDutyAssignmentResponse(), opts);
}
export function useRuleSowGap(programId, opts) {
    return useCmMutation(programId, 'ruleSowGap', async (b)=>(await contractModelClient.ruleSowGap({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RuleSowGapResponse(), opts);
}
export function useCreateSubmission(programId, opts) {
    return useCmMutation(programId, 'createSubmission', async (b)=>(await contractModelClient.createSubmission({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateSubmissionResponse(), opts);
}
export function useWithdrawSubmission(programId, opts) {
    return useCmMutation(programId, 'withdrawSubmission', async (b)=>(await contractModelClient.withdrawSubmission({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.WithdrawSubmissionResponse(), opts);
}
export function useRecordReview(programId, opts) {
    return useCmMutation(programId, 'recordReview', async (b)=>(await contractModelClient.recordReview({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RecordReviewResponse(), opts);
}
export function useUpdateDeliverableSettings(programId, opts) {
    return useCmMutation(programId, 'updateDeliverableSettings', async (b)=>(await contractModelClient.updateDeliverableSettings({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateDeliverableSettingsResponse(), opts);
}
// ---------------------------------------------------------------------------
// SAD-859: team members and the work-product ledger
// ---------------------------------------------------------------------------
export function useWorkProducts(programId, req = {}, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.workProducts(programId, req),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listWorkProducts({
                body: {
                    programId,
                    ...req
                }
            });
            return resp.body?.workProducts ?? [];
        }
    });
}
/** A team's members ("" = every team's). */ export function useWorkstreamMembers(programId, wsId = '') {
    return useQuery({
        queryKey: cmKeys.members(programId, wsId),
        enabled: !!programId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listWorkstreamMembers({
                body: {
                    programId,
                    workstreamId: wsId
                }
            });
            return resp.body?.members ?? [];
        }
    });
}
/**
 * The team's SOW deliverables whose title reads like `title`: a suggestion
 * while logging, never a block. Waits for a few letters and a pause.
 */ export function useWorkProductDuplicates(programId, wsId, title) {
    const [settled, setSettled] = useState(title.trim());
    useEffect(()=>{
        const t = setTimeout(()=>setSettled(title.trim()), 350);
        return ()=>clearTimeout(t);
    }, [
        title
    ]);
    return useQuery({
        queryKey: cmKeys.duplicates(programId, wsId, settled),
        enabled: !!programId && !!wsId && settled.length >= 4,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.findWorkProductDuplicates({
                body: {
                    programId,
                    workstreamId: wsId,
                    title: settled
                }
            });
            return resp.body?.matches ?? [];
        }
    });
}
export function useCreateWorkProduct(programId, opts) {
    return useCmMutation(programId, 'createWorkProduct', async (b)=>(await contractModelClient.createWorkProduct({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateWorkProductResponse(), opts);
}
export function useUpdateWorkProduct(programId, opts) {
    return useCmMutation(programId, 'updateWorkProduct', async (b)=>(await contractModelClient.updateWorkProduct({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateWorkProductResponse(), opts);
}
export function useAssessWorkProducts(programId, opts) {
    return useCmMutation(programId, 'assessWorkProducts', async (b)=>(await contractModelClient.assessWorkProducts({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.AssessWorkProductsResponse(), opts);
}
export function usePromoteWorkProduct(programId, opts) {
    return useCmMutation(programId, 'promoteWorkProduct', async (b)=>(await contractModelClient.promoteWorkProduct({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.PromoteWorkProductResponse(), opts);
}
export function useAddWorkstreamMember(programId, opts) {
    return useCmMutation(programId, 'addWorkstreamMember', async (b)=>(await contractModelClient.addWorkstreamMember({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.AddWorkstreamMemberResponse(), opts);
}
export function useUpdateWorkstreamMember(programId, opts) {
    return useCmMutation(programId, 'updateWorkstreamMember', async (b)=>(await contractModelClient.updateWorkstreamMember({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateWorkstreamMemberResponse(), opts);
}
export function useRemoveWorkstreamMember(programId, opts) {
    return useCmMutation(programId, 'removeWorkstreamMember', async (b)=>(await contractModelClient.removeWorkstreamMember({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RemoveWorkstreamMemberResponse(), opts);
}
// ---------------------------------------------------------------------------
// SAD-863: comment threads. A comment write refreshes its own thread only;
// it changes nothing else in the program.
// ---------------------------------------------------------------------------
export function useComments(programId, target) {
    return useQuery({
        queryKey: cmKeys.comments(programId, target),
        enabled: !!programId && !!target.id,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listComments({
                body: {
                    programId,
                    targetType: target.type,
                    targetId: target.id
                }
            });
            return resp.body?.comments ?? [];
        }
    });
}
function useCommentMutation(programId, target, fn, failure) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: ()=>void qc.invalidateQueries({
                queryKey: cmKeys.comments(programId, target)
            }),
        onError: (err)=>toast.error(apiErrorMessage(err, failure))
    });
}
export function useCreateComment(programId, target) {
    return useCommentMutation(programId, target, (v)=>contractModelClient.createComment({
            body: {
                programId,
                targetType: target.type,
                targetId: target.id,
                ...v
            }
        }), 'Could not post the comment');
}
export function useUpdateComment(programId, target) {
    return useCommentMutation(programId, target, (v)=>contractModelClient.updateComment({
            body: {
                programId,
                ...v
            }
        }), 'Could not save the comment');
}
export function useDeleteComment(programId, target) {
    return useCommentMutation(programId, target, (commentId)=>contractModelClient.deleteComment({
            body: {
                programId,
                commentId
            }
        }), 'Could not delete the comment');
}
export function useSetCommentUpvote(programId, target) {
    return useCommentMutation(programId, target, (v)=>contractModelClient.setCommentUpvote({
            body: {
                programId,
                ...v
            }
        }), 'Could not record the upvote');
}
